<script setup lang="ts">
const { t: $t } = useI18n()

import 'viewerjs/dist/viewer.css'
import * as viewer from 'v-viewer'
import type { DataTableColumns } from 'naive-ui'
import { joinURL } from 'ufo'
import type { ApiMachineryGetById, ApiMachineryUVVReportAllByMachineryId, ApiMachineryUpdateMachineryUVVDocuments, FilePath } from '~/types'

interface User {
  name: string | null
  email: string
}

export interface SignedUVV {
  id: string
  signedUvv: string | null
  customerName: string | undefined
}

const props = defineProps<{ machinery: ApiMachineryGetById, isCreateDisabled?: boolean, isUpdateDisabled?: boolean }>()
const { machineryUVVReport } = useQuery()
const { data: uvvReports, error, isLoading } = machineryUVVReport.allByMachineryId(props.machinery.id)

const uvvDeletePayload = shallowRef<null | string>(null)
const uvvPopupPayload = shallowRef<null | { machineryId: string, id?: string, readOnly?: boolean, isForeignMachinery?: boolean }>(null)
const uploadUVVDocumentsPayload = shallowRef<null | ApiMachineryUpdateMachineryUVVDocuments>(null)
const signedUVVPayload = shallowRef<SignedUVV | null>(null)

const { hasAnyAssignedRole } = useAuthorization()
const canEditUVV = hasAnyAssignedRole(['departmentLead', 'workshopEmployee', 'specialRoleBenni', 'receptionist', 'storageEmployee', 'workshopOfficeEmployee'])
const latestUVVInformation = computed((): { user: User, inspectionDate: Date } | null => {
  if (uvvReports.value && uvvReports.value.length > 0) {
    return {
      user: uvvReports.value[0].updatedBy ?? uvvReports.value[0].createdBy,
      inspectionDate: uvvReports.value[0].inspectionDate,
    }
  } else if (props.machinery.lastUVVDate && props.machinery.lastUVVDateUpdatedBy) {
    return {
      user: props.machinery.lastUVVDateUpdatedBy,
      inspectionDate: props.machinery.lastUVVDate,
    }
  }
  return null
})

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()
const deleteOne = useMutation({
  mutationFn: $trpc.machineryUVVReport.delete.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('machineryUVVReport.notification.delete.error') }),
  onSuccess: async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['machineryUVVReport'] }),
      queryClient.invalidateQueries({ queryKey: ['machines'] }),
    ])
    notification.success({ title: $t('machineryUVVReport.notification.delete.success'), duration: 4500 })
    uvvDeletePayload.value = null
  },
})
const updateMachineryUVVDocuments = useMutation({
  mutationFn: $trpc.machinery.updateUVVDocuments.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('machineryUVVReport.notification.update.error') }),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['machines'] })
    notification.success({ title: $t('machineryUVVReport.notification.update.success'), duration: 4500 })
    uploadUVVDocumentsPayload.value = null
  },
})

/** Table */
const columns = computed((): DataTableColumns<ApiMachineryUVVReportAllByMachineryId> => [
  {
    title: $t('machinery.uvvReport.inspectionDate.label'),
    key: 'inspectionDate',
    minWidth: 160,
    render: report => useDateAsString(report.inspectionDate, 'dd.MM.yyyy'),
  },
  {
    title: $t('common.field.createdAt.name'),
    key: 'createdAt',
    minWidth: 120,
    render: report => useDateAsString(report.createdAt),
  },
  {
    title: $t('common.field.createdBy.name'),
    key: 'createdBy',
    minWidth: 120,
    render: report => report.createdBy.name ?? report.createdBy.email,
  },
  {
    title: $t('common.field.updatedAt.name'),
    key: 'updatedAt',
    minWidth: 120,
    render: report => report.updatedAt ? useDateAsString(report.updatedAt) : $t('general.notApplicable'),
  },
  {
    title: $t('common.field.updatedBy.name'),
    key: 'updatedBy',
    minWidth: 140,
    render: report => report.updatedBy?.name ?? report.updatedBy?.email ?? $t('general.notApplicable'),
  },
  {
    title: $t('machinery.uvvReport.customerAcceptanceDate.label'),
    key: 'signedUvvUploadedAt',
    minWidth: 180,
    render: report => report.signedUvvUploadedAt ? useDateAsString(report.signedUvvUploadedAt, 'dd.MM.yyyy') : $t('general.notApplicable'),
  },
])

function buttonsToShow(report: ApiMachineryUVVReportAllByMachineryId) {
  const buttons: Action[] = ['pdf']
  if (report.photos && report.photos.length > 0) {
    buttons.push('gallery')
  }
  if (canEditUVV.value) {
    buttons.push('update', 'delete')
  } else {
    buttons.push('view')
  }
  if (canEditUVV.value && props.machinery.isForeignMachinery) {
    buttons.push('customerAcceptance')
  }
  return buttons
}

function openReportPDf(reportId: string) {
  window.open(joinURL('/api/pdf/machinery/uvvReport', reportId), '_blank')
}

function previewImgObject(images: FilePath[], index = 0) {
  if (!images || images.length === 0) {
    return
  }
  viewer.api({
    images: images.map(photo => ({
      'data-source': photo.path,
      'src': photo.path,
    })),
    options: {
      toolbar: true,
      url: 'data-source',
      initialViewIndex: index,
    },
  })
}
</script>

<template>
  <TheDataCard :error :is-loading>
    <template #header>
      <div class="CardHeader">
        <h2>UVV</h2>
        <n-popover v-if="isCreateDisabled" trigger="hover" placement="bottom">
          <template #trigger>
            <n-button v-if="!isUpdateDisabled && canEditUVV" :disabled="isCreateDisabled" @click="uvvPopupPayload = { machineryId: machinery.id, isForeignMachinery: machinery.isForeignMachinery }">
              <template #icon>
                <Icon name="material-symbols:add-circle-outline" />
              </template>
              {{ $t("button.create") }}
            </n-button>
          </template>
          {{ $t("machinery.detailsPage.uvv.create.hint") }}
        </n-popover>
        <n-button v-else-if="!isUpdateDisabled && canEditUVV" @click="uvvPopupPayload = { machineryId: machinery.id, isForeignMachinery: machinery.isForeignMachinery }">
          <template #icon>
            <Icon name="material-symbols:add-circle-outline" />
          </template>
          {{ $t("button.create") }}
        </n-button>
      </div>
    </template>

    <div class="space-y-8">
      <!-- Latest UVV report info -->
      <div v-if="machinery.nextUVVRequiredDate && latestUVVInformation" class="space-y-2">
        <p class="text-sm font-semibold">
          {{ $t('machinery.detailsPage.uvv.nextInspectionDate') }} {{ useDateAsString(machinery.nextUVVRequiredDate, 'dd.MM.yyyy') }}
        </p>
        <p class="text-sm text-gray-500">
          {{ $t('machinery.detailsPage.uvv.lastUpdatedBy') }} {{ latestUVVInformation.user.name }} {{ latestUVVInformation.user.email }} ({{ useDateAsString(latestUVVInformation.inspectionDate, 'dd.MM.yyyy') }})
        </p>
      </div>

      <!-- UVV Reports table -->
      <TableView
        :data="uvvReports"
        :columns
        :is-loading
        :action-buttons="buttonsToShow"
        :scroll-x="1200"
        @pdf="(report) => openReportPDf(report.id)"
        @view="(report) => uvvPopupPayload = { machineryId: machinery.id, id: report.id, readOnly: true, isForeignMachinery: machinery.isForeignMachinery }"
        @update="(report) => uvvPopupPayload = { machineryId: machinery.id, id: report.id, isForeignMachinery: machinery.isForeignMachinery }"
        @delete="(report) => uvvDeletePayload = report.id"
        @customer-acceptance="(report) => signedUVVPayload = { id: report.id, signedUvv: report.signedUvv, customerName: machinery.foreignMachineryCustomer?.name }"
        @gallery="(report) => previewImgObject(report.photos)"
      />

      <!-- UVV related Documents or old UVV reports -->
      <div>
        <div class="CardHeader">
          <h2>
            {{ $t('machinery.detailsPage.uvv.uploadedUVVDocuments') }}
          </h2>
          <n-button v-if="!isUpdateDisabled && canEditUVV" @click="uploadUVVDocumentsPayload = machinery">
            <template #icon>
              <Icon name="material-symbols:edit-outline-rounded" />
            </template>
            {{ $t("button.update") }}
          </n-button>
        </div>
        <FileView
          :files="machinery.documentUVVFiles"
        />
      </div>
    </div>
  </TheDataCard>

  <MachineryUVVReportCreateOrEditPopup
    v-if="uvvPopupPayload"
    :payload="uvvPopupPayload"
    @close="uvvPopupPayload = null"
  />
  <TheConfirmPopup v-if="uvvDeletePayload" @close="uvvDeletePayload = null" @confirm="deleteOne.mutate({ id: uvvDeletePayload })">
    {{ $t('machinery.uvvReport.delete.confirmation') }}
  </TheConfirmPopup>
  <MachineryPopupUploadUVVDocuments
    v-if="uploadUVVDocumentsPayload"
    :machinery="uploadUVVDocumentsPayload"
    @update="updateMachineryUVVDocuments.mutate"
    @close="uploadUVVDocumentsPayload = null"
  />

  <MachineryDetailsPopupUploadSignedUVV
    v-if="signedUVVPayload"
    :payload="signedUVVPayload"
    @close="signedUVVPayload = null"
  />
</template>

<style scoped>
.CardHeader {
  @apply  w-full flex justify-between mb-5 items-center md:items-start
}
.CardHeader h2 {
  @apply text-lg font-semibold
}
.DetailsColumn {
  @apply md:w-[400px] flex-grow;
}
</style>
